import { Component, computed, effect, inject, input, OnInit, output, signal } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { CreateMCPConfig, McpService, MCPTool, UpdateMCPConfig } from '@shared-angular/services/mcp.service';
import { MCPConfig } from '@shared/index';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { KeyFilterModule } from 'primeng/keyfilter';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { ProjectSecretService } from 'src/app/shared/services/project-secret.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';

const authMethods = [
  { label: "Access Token / API key", value: "token" },
  { label: "None", value: "none" },
  { label: "Custom headers", value: "header" }
] as const;

type ToolApproval = "always" | "never";
type GlobalToolApproval = ToolApproval | "custom";

type RequireApprovalConfig = {
  always?: { tool_names?: string[] };
  never?: { tool_names?: string[] };
};

const toolApprovalOptions: { label: string; value: ToolApproval }[] = [
  { label: $localize`:@@mcp-approval-always:Immer`, value: "always" },
  { label: $localize`:@@mcp-approval-never:Nie`, value: "never" }
];

const globalToolApprovalOptions: { label: string; value: GlobalToolApproval; disabled?: boolean }[] = [
  ...toolApprovalOptions,
  { label: $localize`:@@mcp-approval-custom:Benutzerdefiniert`, value: "custom", disabled: true }
];
@Component({
  selector: 'app-mcp-add-edit-external',
  imports: [ButtonModule, DrawerModule, FloatLabelModule, FormsModule, InputTextModule, KeyFilterModule, ReactiveFormsModule, SelectModule, TableModule, TooltipModule, ToggleSwitchModule],
  templateUrl: './mcp-add-edit-external.component.html',
  styleUrl: './mcp-add-edit-external.component.scss'
})
export class McpAddEditExternalComponent implements OnInit {
  public readonly visible = input<boolean>(false);
  public readonly visibleChange = output<boolean>();

  public readonly edit = input.required<boolean>();
  public readonly mcpConfig = input<MCPConfig>();

  public form: FormGroup;

  public serverTools = signal<MCPTool[]>([]);
  public selectedServerTools = signal<MCPTool[]>([]);
  public toolsLoadState = signal<'idle' | 'loading' | 'empty' | 'error'>('idle');

  public readonly server_label_regex: RegExp = /^[a-zA-Z0-9_-]{1,64}$/;
  // TODO: Filter entfernen, wenn Header wieder auswählbar sein sollen
  public readonly authMethods = [...authMethods];
  public readonly toolApprovalOptions = [...toolApprovalOptions];
  public readonly globalToolApprovalOptions = [...globalToolApprovalOptions];
  public selectedAuthMethod = signal<typeof authMethods[number]["value"]>("none");
  public toolApprovals = signal<Record<string, ToolApproval>>({});

  public globalToolApproval = computed<GlobalToolApproval>(() => {
    const selected = this.selectedServerTools().map(t => t.name);
    if (selected.length === 0) return "never";
    const approvals = this.toolApprovals();
    if (selected.every(toolName => (approvals[toolName] ?? "never") === "always")) return "always";
    if (selected.every(toolName => (approvals[toolName] ?? "never") === "never")) return "never";
    return "custom";
  });
  private headerValidationSub?: Subscription;

  public get headerRows(): FormArray {
    return this.form.get('tool.headers') as FormArray;
  }

  public addHeaderRow(): void {
    this.headerRows.push(this.fb.group({
      key: this.fb.control('', { validators: [Validators.required] }),
      value: this.fb.control('', { validators: [Validators.required] })
    }));
    this.syncDuplicateHeaderErrors(this.headerRows);
  }

  public removeHeaderRow(index: number): void {
    this.headerRows.removeAt(index);
    this.syncDuplicateHeaderErrors(this.headerRows);
  }

  public isDuplicateHeader(index: number): boolean {
    const keyControl = (this.headerRows.at(index) as FormGroup).get('key');
    return !!keyControl?.hasError('duplicateHeaderName');
  }

  public secretService = inject(ProjectSecretService);
  public rss = inject(RouteShareService);
  public mcpService = inject(McpService);

  constructor(private fb: FormBuilder) {
    const headerControl = this.fb.array([
      this.fb.group({
        key: this.fb.control('', { validators: [Validators.required] }),
        value: this.fb.control('', { validators: [Validators.required] })
      })
    ]);
    const authorizationControl = this.fb.control("", {
      validators: [
        (control: AbstractControl): ValidationErrors | null => {
          if (control.value) {
            return null;
          }
          return { required: true };
        }
      ]
    });
    this.form = this.fb.group({
      type: this.fb.control('external'),
      enabled: this.fb.control(true),
      tool: this.fb.group({
        type: this.fb.control("mcp"),
        server_label: this.fb.control("", { validators: [Validators.required] }),
        server_description: this.fb.control(""),
        server_url: this.fb.control("", { validators: [Validators.required] }),
        authorization: authorizationControl,
        headers: headerControl,
        allowed_tools: [[]],
        require_approval: this.fb.group({
          always: this.fb.group({
            tool_names: [[]]
          }),
          never: this.fb.group({
            tool_names: [[]]
          })
        })
      })
    });
    this.setupHeaderValidation(headerControl);

    effect(() => {
      const authType = this.selectedAuthMethod();
      const toolGroup = this.form.get('tool') as FormGroup;
      switch (authType) {
        case "none":
          this.headerValidationSub?.unsubscribe();
          toolGroup.removeControl("authorization");
          toolGroup.removeControl("headers");
          return;
        case "header":
          {
            toolGroup.removeControl("authorization");
            const freshHeaderControl = this.fb.array([
              this.fb.group({
                key: this.fb.control('', { validators: [Validators.required] }),
                value: this.fb.control('', { validators: [Validators.required] })
              })
            ]);
            toolGroup.addControl("headers", freshHeaderControl);
            this.setupHeaderValidation(freshHeaderControl);
            return;
          }
        case "token":
          this.headerValidationSub?.unsubscribe();
          toolGroup.removeControl("headers");
          toolGroup.addControl("authorization", authorizationControl);
          return;
      }
    });

    effect(() => {
      const selected = this.selectedServerTools().map(t => t.name);
      const toolApprovals = this.toolApprovals();
      const always = selected.filter(toolName => toolApprovals[toolName] === "always");
      const never = selected.filter(toolName => toolApprovals[toolName] !== "always");

      this.form.get("tool")?.get("allowed_tools")?.setValue(selected);
      this.form.get("tool")?.get("require_approval")?.get("always")?.get("tool_names")?.setValue(always);
      this.form.get("tool")?.get("require_approval")?.get("never")?.get("tool_names")?.setValue(never);
    });
  }

  public async ngOnInit(): Promise<void> {
    this.secretService.selectedProjectId = this.rss.botConfig.projectId ?? "";
    const mcpConfig = this.mcpConfig();
    if (this.edit() && mcpConfig && mcpConfig.type === "external") {
      if (mcpConfig.tool.authorization) {
        this.selectedAuthMethod.set("token");
      } else if (mcpConfig.tool.headers) {
        this.selectedAuthMethod.set("header");
      } else {
        this.selectedAuthMethod.set("none");
      }
      if (mcpConfig.tool.server_url) {
        await this.loadSelectedServerTools(mcpConfig.tool.server_url);
        this.selectedServerTools.set(this.serverTools().filter(t => {
          const mcpConfigValue = this.mcpConfig();
          if (mcpConfigValue) {
            if (mcpConfigValue.type === "managed") {
              return mcpConfigValue.allowed_tools.includes(t.name);
            } else {
              return mcpConfigValue.tool.allowed_tools.includes(t.name);
            }
          }
          return false;
        }));
      }

      const configuredApprovals: Record<string, ToolApproval> = {};
      const requireApproval = mcpConfig.tool.require_approval as RequireApprovalConfig | undefined;
      const alwaysToolNames = requireApproval?.always?.tool_names;
      const neverToolNames = requireApproval?.never?.tool_names;

      for (const toolName of alwaysToolNames ?? []) {
        configuredApprovals[toolName] = "always";
      }
      for (const toolName of neverToolNames ?? []) {
        configuredApprovals[toolName] = "never";
      }
      this.toolApprovals.set(configuredApprovals);

      this.form.get("enabled")?.setValue(mcpConfig.enabled);
      const toolGroup = this.form.get("tool");
      toolGroup?.get("server_label")?.setValue(mcpConfig.tool.server_label);
      toolGroup?.get("server_description")?.setValue(mcpConfig.tool.server_description);
      toolGroup?.get("server_url")?.setValue(mcpConfig.tool.server_url);
      if (mcpConfig.tool.authorization) {
        toolGroup?.get("authorization")?.setValue(mcpConfig.tool.authorization);
      }
      if (mcpConfig.tool.headers) {
        const headersArray = Object.entries(mcpConfig.tool.headers).map(([key, value]) => ({
          key,
          value
        }));
        const headersFormArray = toolGroup?.get("headers") as FormArray;
        headersFormArray.clear();
        headersArray.forEach(header => {
          headersFormArray.push(this.fb.group({
            key: this.fb.control(header.key, { validators: [Validators.required] }),
            value: this.fb.control(header.value, { validators: [Validators.required] })
          }));
        });
        this.syncDuplicateHeaderErrors(headersFormArray);
      }
    }
  }


  public async loadSelectedServerTools(url: string) {
    if (url.trim() === "") return;
    this.toolsLoadState.set('loading');
    try {
      const toolGroup = this.form.get('tool') as FormGroup;
      const authorization = this.selectedAuthMethod() === "token"
        ? (toolGroup.get('authorization')?.value as string || undefined)
        : undefined;
      const headers = this.selectedAuthMethod() === "header"
        ? this.buildHeadersRecord()
        : undefined;
      const res = await this.mcpService.loadMcpTools(this.rss.botConfig._id, url, authorization, headers);
      if (res.length === 0) {
        this.serverTools.set([]);
        this.selectedServerTools.set([]);
        this.toolsLoadState.set('empty');
      } else {
        this.serverTools.set(res);
        this.selectedServerTools.set(res);
        this.toolsLoadState.set('idle');
      }
    } catch (e: unknown) {
      console.error(e);
      this.serverTools.set([]);
      this.selectedServerTools.set([]);
      this.toolsLoadState.set('error');
    }
  }

  // Re-fetches the tool list once a secret is selected, since private servers only expose tools with valid auth.
  public onAuthSecretChanged(): void {
    const url = (this.form.get('tool')?.get('server_url')?.value as string | undefined) ?? "";
    void this.loadSelectedServerTools(url);
  }

  private buildHeadersRecord(): Record<string, string> | undefined {
    const rows = (this.form.get('tool')?.get('headers') as FormArray | null)?.value as { key: string; value: string }[] | undefined;
    if (!rows) return undefined;
    const record: Record<string, string> = {};
    for (const row of rows) {
      if (row.key && row.value) {
        record[row.key] = row.value;
      }
    }
    return Object.keys(record).length > 0 ? record : undefined;
  }

  public async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.value as { tool: { headers: unknown[] | Record<string, string> } };
    // Convert headers FormArray back to object format
    if (Array.isArray(formValue.tool.headers)) {
      formValue.tool.headers = (formValue.tool.headers as { key: string; value: string }[]).reduce<Record<string, string>>(
        (acc, header) => {
          acc[header.key] = header.value;
          return acc;
        },
        {}
      );
    }
    if (!this.edit()) {
      await this.mcpService.createMcp(this.rss.botConfig._id, formValue as CreateMCPConfig);
    } else {
      const mcpConfig = this.mcpConfig();
      if (mcpConfig) {
        await this.mcpService.updateMcp(this.rss.botConfig._id, mcpConfig._id, formValue as UpdateMCPConfig);
      }
    }
    this.visibleChange.emit(false);
  }

  public getToolApproval(toolName: string): ToolApproval {
    return this.toolApprovals()[toolName] ?? "never";
  }

  public setToolApproval(toolName: string, approval: ToolApproval): void {
    this.toolApprovals.update(current => ({
      ...current,
      [toolName]: approval
    }));
  }

  // "custom" ist nur ein Anzeigewert fuer gemischte Zustaende und kein tatsaechlicher Auswahlwert.
  public setGlobalToolApproval(approval: GlobalToolApproval): void {
    if (approval === "custom") return;
    const selected = this.selectedServerTools().map(t => t.name);
    this.toolApprovals.update(current => {
      const updated = { ...current };
      for (const toolName of selected) {
        updated[toolName] = approval;
      }
      return updated;
    });
  }

  private setupHeaderValidation(headersArray: FormArray): void {
    this.headerValidationSub?.unsubscribe();
    this.headerValidationSub = headersArray.valueChanges.subscribe(() => {
      this.syncDuplicateHeaderErrors(headersArray);
    });
    this.syncDuplicateHeaderErrors(headersArray);
  }

  private syncDuplicateHeaderErrors(headersArray: FormArray): void {
    const keyCounts = new Map<string, number>();
    headersArray.controls.forEach(control => {
      const keyControl = (control as FormGroup).get('key');
      const normalizedKey = (keyControl?.value as string | undefined)?.trim().toLowerCase();
      if (!normalizedKey) {
        return;
      }
      keyCounts.set(normalizedKey, (keyCounts.get(normalizedKey) ?? 0) + 1);
    });

    headersArray.controls.forEach(control => {
      const keyControl = (control as FormGroup).get('key');
      if (!keyControl) {
        return;
      }
      const normalizedKey = (keyControl.value as string | undefined)?.trim().toLowerCase();
      const hasDuplicate = !!normalizedKey && (keyCounts.get(normalizedKey) ?? 0) > 1;
      const currentErrors = keyControl.errors ?? {};

      if (hasDuplicate) {
        keyControl.setErrors({ ...currentErrors, duplicateHeaderName: true });
        return;
      }

      if (!currentErrors['duplicateHeaderName']) {
        return;
      }

      // Keep other validator errors (e.g. required) intact when duplicate is resolved.
      const { duplicateHeaderName, ...remainingErrors } = currentErrors;
      void duplicateHeaderName;
      keyControl.setErrors(Object.keys(remainingErrors).length > 0 ? remainingErrors : null);
    });
  }
}
